import { useQueryClient } from '@tanstack/react-query'
import { FaqCategoryItem, FaqPageLocationState } from 'Modules'
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import Breadcrumb from 'component/breadcrumb/breadcrumb'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { faqCategoryKeys, useFaqCategoryList } from 'hooks/faq'
import { FaqCategoryFormModal, FaqManagementPanel } from 'modules/faq'

import './style.scss'

const FaqPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [modalVisible, setModalVisible] = useState(false)
  const [editingItem, setEditingItem] = useState<FaqCategoryItem | undefined>()
  const [search, setSearch] = useState('')
  const [categoryVisibility, setCategoryVisibility] = useState('')
  const [articleStatus, setArticleStatus] = useState('')
  const [expandCategoryId, setExpandCategoryId] = useState<string | null>(
    () => (location.state as FaqPageLocationState | null)?.expandCategoryId ?? null
  )

  // Consume the router state once so a refresh does not re-apply it
  useEffect(() => {
    if ((location.state as FaqPageLocationState | null)?.expandCategoryId) {
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location.pathname, location.state, navigate])

  const listParams = { search, articleStatus, categoryVisibility }
  const { total, maxCategories, refetch } = useFaqCategoryList(listParams)

  const refreshFaqList = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: faqCategoryKeys.lists() })
  }, [queryClient])

  // Reload clears search/filters then refetches the full list (MN-4486).
  const handleReloadFilters = useCallback(() => {
    setSearch('')
    setCategoryVisibility('')
    setArticleStatus('')
    refreshFaqList()
  }, [refreshFaqList])

  const handleOpenCreate = () => {
    setEditingItem(undefined)
    setModalVisible(true)
  }

  const handleEdit = (item: FaqCategoryItem) => {
    setEditingItem(item)
    setModalVisible(true)
  }

  const handleCloseModal = () => {
    setEditingItem(undefined)
    setModalVisible(false)
  }

  const handleExpandCategoryHandled = useCallback(() => {
    setExpandCategoryId(null)
  }, [])

  return (
    <div className="main-section faq-management-page">
      <Breadcrumb additionalItems={[BREADCRUMBS.FAQ]} />
      <div className="main-title">FAQ Management</div>
      <p className="faq-capacity">
        Categories: <strong>{total}</strong> / {maxCategories} max
      </p>

      <FaqManagementPanel
        search={search}
        onSearchChange={setSearch}
        categoryVisibility={categoryVisibility}
        onCategoryVisibilityChange={setCategoryVisibility}
        articleStatus={articleStatus}
        onArticleStatusChange={setArticleStatus}
        onEditCategory={handleEdit}
        onCreateCategory={handleOpenCreate}
        onRefresh={refreshFaqList}
        onReloadFilters={handleReloadFilters}
        expandCategoryId={expandCategoryId}
        onExpandCategoryHandled={handleExpandCategoryHandled}
      />

      <FaqCategoryFormModal
        visible={modalVisible}
        editingItem={editingItem}
        onClose={handleCloseModal}
        onSuccess={(item) => {
          void refetch()
          if (item?._id) {
            setExpandCategoryId(item._id)
          }
        }}
      />
    </div>
  )
}

export default FaqPage
